import { Link } from "react-router-dom";
import { MapPin, Star, Train, Plane, Moon, Users, ArrowUpRight } from "lucide-react";
import { CTAButton } from "@/components/CTAButton";
import { onImgError } from "@/lib/images";
import { formatEventDate } from "@/lib/dates";

export function VenueCard({ venue, to }) {
  return (
    <Link
      to={to || `/venues/${venue.slug}`}
      data-testid={`venue-card-${venue.slug}`}
      className="group relative block overflow-hidden rounded-xl card-shadow bg-card border border-black/5 transition-transform duration-300 ease-out hover:-translate-y-1"
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img src={venue.image_url} alt={venue.image_alt || venue.name} loading="lazy" onError={onImgError}
          className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105" />
        <div className="absolute inset-0 img-overlay" />
        <div className="absolute bottom-0 left-0 p-5">
          <div className="overline text-white/70 flex items-center gap-1.5 mb-1"><MapPin className="h-3 w-3" />{venue.city_slug?.replace(/-/g, " ")}</div>
          <h3 className="font-heading text-xl font-bold tracking-tight text-white drop-shadow">{venue.name}</h3>
        </div>
        <div className="absolute top-4 right-4 grid h-9 w-9 place-items-center rounded-full bg-white/90 text-ink opacity-0 group-hover:opacity-100 transition-opacity">
          <ArrowUpRight className="h-4 w-4" />
        </div>
      </div>
    </Link>
  );
}

export function EventCard({ event }) {
  const dateLabel = formatEventDate(event.date, event.timezone);
  return (
    <Link
      to={`/events/${event.slug}`}
      data-testid={`event-card-${event.slug}`}
      className="group flex w-full min-w-0 items-center gap-4 rounded-xl bg-card border border-black/5 card-shadow p-3 transition-transform duration-300 hover:-translate-y-1"
    >
      <div className="relative h-20 w-20 sm:w-24 shrink-0 overflow-hidden rounded-lg">
        <img src={event.image_url} alt={event.image_alt || event.title} loading="lazy" onError={onImgError} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="overline text-primary mb-1">{event.event_type}</div>
        <h4 className="font-heading font-bold text-ink leading-tight truncate">{event.title}</h4>
        {dateLabel && <p className="text-xs text-muted-foreground mt-1">{dateLabel}</p>}
      </div>
      <ArrowUpRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
    </Link>
  );
}

export function CityCard({ city }) {
  return (
    <Link
      to={`/cities/${city.slug}`}
      data-testid={`city-card-${city.slug}`}
      className="group relative block overflow-hidden rounded-xl h-full min-h-[220px] transition-transform duration-300 hover:-translate-y-1"
    >
      <img src={city.image_url} alt={city.image_alt || city.name} loading="lazy" onError={onImgError} className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
      <div className="absolute inset-0 img-overlay" />
      <div className="absolute bottom-0 p-6">
        <div className="overline text-white/70 mb-1">{city.state}</div>
        <h3 className="font-heading text-2xl font-bold tracking-tight text-white drop-shadow">{city.name}</h3>
      </div>
    </Link>
  );
}

const scoreIcon = { transit: Train, airport: Plane, nightlife: Moon, family: Users };

export function StayAreaCard({ area, ctx = {}, index = 0 }) {
  const badge = area._badge || area.best_for;
  return (
    <article
      data-testid={`stay-area-card-${area.slug}`}
      className="group overflow-hidden rounded-xl bg-card border border-black/5 card-shadow transition-transform duration-300 hover:-translate-y-1 animate-fade-up"
      style={{ animationDelay: `${index * 60}ms` }}
    >
      <div className="relative aspect-[16/9] overflow-hidden">
        <img src={area.image_url} alt={area.image_alt || area.name} loading="lazy" onError={onImgError} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        <div className="absolute inset-0 img-overlay" />
        {badge && (
          <span className="absolute top-4 left-4 rounded-full bg-primary px-3 py-1 text-xs font-heading font-bold text-primary-foreground shadow">
            {badge}
          </span>
        )}
        <div className="absolute bottom-0 left-0 p-5">
          <h3 className="font-heading text-2xl font-bold tracking-tight text-white drop-shadow">{area.name}</h3>
          {area._access && <p className="text-xs text-white/80 mt-0.5">{area._access}</p>}
        </div>
      </div>

      <div className="p-5">
        <p className="text-sm text-muted-foreground leading-relaxed">{area.summary}</p>

        <div className="mt-4 grid grid-cols-2 gap-3">
          {area.pros?.length > 0 && (
            <div>
              <div className="overline text-emerald-600 mb-1.5">Pros</div>
              <ul className="space-y-1 text-xs text-foreground/80">
                {area.pros.slice(0, 3).map((p, i) => <li key={i}>• {p}</li>)}
              </ul>
            </div>
          )}
          {area.cons?.length > 0 && (
            <div>
              <div className="overline text-rose-500 mb-1.5">Cons</div>
              <ul className="space-y-1 text-xs text-foreground/80">
                {area.cons.slice(0, 3).map((c, i) => <li key={i}>• {c}</li>)}
              </ul>
            </div>
          )}
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <Meta icon={Moon} label={`Nightlife ${area.nightlife_score}/10`} />
          <Meta icon={Users} label={area.price_positioning} />
          <Meta icon={Plane} label={area.airport_convenience} truncate />
        </div>

        {area.editorial_recommendation && (
          <div className="mt-4 rounded-lg bg-muted/60 p-3 border-l-2 border-primary">
            <div className="overline text-primary mb-1">Hotelstrips take</div>
            <p className="text-xs text-foreground/80 leading-relaxed">{area.editorial_recommendation}</p>
          </div>
        )}

        <div className="mt-5">
          {area._hotel_link_scope === "none" ? (
            <span data-testid={`stay-area-cta-${area.slug}-unavailable`}
              className="inline-flex w-full items-center justify-center rounded-full bg-muted px-4 py-2 text-sm font-semibold text-muted-foreground cursor-not-allowed">
              Hotel search unavailable
            </span>
          ) : (
            <CTAButton
              productType="hotels"
              size="sm"
              variant="ghost"
              className="w-full"
              testId={`stay-area-cta-${area.slug}`}
              label={area._hotel_link_scope === "neighborhood" ? `Find hotels in ${area.name}` : "Search hotels · city-wide"}
              ctx={{ ...ctx, stayArea: area.slug, city: area.city_slug, position: "stay_area_card" }}
            />
          )}
        </div>
      </div>
    </article>
  );
}

function Meta({ icon: Icon, label, truncate }) {
  if (!label) return null;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-[11px] font-medium text-muted-foreground ${truncate ? "max-w-[160px] truncate" : ""}`}>
      <Icon className="h-3 w-3 shrink-0" /> {label}
    </span>
  );
}

export { scoreIcon };
